import { useState } from 'react'
import { Badge, Button, Form, Nav, Spinner, Table } from 'react-bootstrap'
import { GitCompareArrows, Link2, Sparkles, X } from 'lucide-react'
import { Link } from 'react-router-dom'
import DistributorInlineField from './DistributorInlineField'
import {
  DistributorDto,
  DistributorProductReportDto,
  DistributorReportDto,
  RulePackDto,
  type DivisionDto,
} from '../services/api'

export type DistributorDetailsDraft = {
  name: string
  address: string
  phone: string
  companyNumber: string
  marketingEmail: string
  primaryContactEmail: string
  secondaryContactEmail: string
  notes: string
  websiteUrl: string
  websiteCountry: string
  divisionId: string
  businessEntityId: string
  ownerAppUserId: string
  market: string
  approvalStatus: string
  organizationType: DistributorDto['organizationType']
}

export type ContactAuditDraft = {
  contactedUtc: string
  reason: string
  contactMethod: string
  notes: string
}

type SidebarTab = 'details' | 'audit' | 'products' | 'checks' | 'notes'

type Props = {
  distributor: DistributorDto
  detailsDraft: DistributorDetailsDraft
  onDetailsChange: (updater: (d: DistributorDetailsDraft) => DistributorDetailsDraft) => void
  detailsStatus: string | null
  aiEnrichStatus: string | null
  contactAuditDraft: ContactAuditDraft
  onContactAuditChange: (updater: (d: ContactAuditDraft) => ContactAuditDraft) => void
  divisions: DivisionDto[]
  businessEntities: Array<{ id: string; name: string }>
  users: Array<{ id: string; displayName?: string; email?: string; userId?: string }>
  distributorReport?: DistributorReportDto
  isReportLoading: boolean
  productDueDateByProductId: Record<string, string>
  onProductDueDateChange: (productId: string, value: string) => void
  orphanedProductIds: Set<string>
  onClose: () => void
  onSaveDetails: () => void
  onAiEnrich: () => void
  onLogContact: () => void
  onCompareProduct: (productId: string) => void
  onLinkProduct: (product: DistributorProductReportDto) => void
  onSaveProductDueDate: (productId: string) => void
  isSavingDetails: boolean
  isAiEnriching: boolean
  isLoggingContact: boolean
  isSavingProductAudit: boolean
  rulePacks: RulePackDto[]
  distributorCheckRulePackId: string
  onDistributorCheckRulePackChange: (rulePackId: string) => void
  onRunDistributorCheck: () => void
  isRunningDistributorCheck: boolean
  distributorCheckStatus: string | null
  latestDistributorCheckRunId: string | null
  onWebsiteUrlBlur: () => void
  onArchive?: () => void
  onDelete?: () => void
  isArchiving?: boolean
  isDeleting?: boolean
  /** When true, render as permanent right panel (not slide-over). */
  embedded?: boolean
}

export default function DistributorDetailSidebar({
  distributor,
  detailsDraft,
  onDetailsChange,
  detailsStatus,
  aiEnrichStatus,
  contactAuditDraft,
  onContactAuditChange,
  divisions,
  businessEntities,
  users,
  distributorReport,
  isReportLoading,
  productDueDateByProductId,
  onProductDueDateChange,
  orphanedProductIds,
  onClose,
  onSaveDetails,
  onAiEnrich,
  onLogContact,
  onCompareProduct,
  onLinkProduct,
  onSaveProductDueDate,
  isSavingDetails,
  isAiEnriching,
  isLoggingContact,
  isSavingProductAudit,
  rulePacks,
  distributorCheckRulePackId,
  onDistributorCheckRulePackChange,
  onRunDistributorCheck: _onRunDistributorCheck,
  isRunningDistributorCheck: _isRunningDistributorCheck,
  distributorCheckStatus,
  latestDistributorCheckRunId,
  onWebsiteUrlBlur,
  onArchive,
  onDelete,
  isArchiving,
  isDeleting,
  embedded = false,
}: Props) {
  const [tab, setTab] = useState<SidebarTab>('details')

  const websiteDisplay = detailsDraft.websiteUrl || `https://${distributor.domain}/`
  const orgLabel =
    detailsDraft.organizationType === 'AccreditedDistributor'
      ? 'Accredited distributor'
      : detailsDraft.organizationType === 'IndependentReseller'
        ? 'Independent supplier'
        : 'Unclassified'

  return (
    <aside className={`distributor-sidebar${embedded ? ' distributor-sidebar--embedded' : ''}`} data-tour="distributors-detail" onClick={(e) => e.stopPropagation()}>
      <div className="sidebar-header">
        <div>
          <h5>{detailsDraft.name || distributor.name}</h5>
          <div className="sidebar-header-sub">{orgLabel} · {websiteDisplay}</div>
        </div>
        <button type="button" className="sidebar-close" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>
      </div>

      <Nav variant="tabs" className="distributor-sidebar-tabs" activeKey={tab} onSelect={(k) => setTab((k as SidebarTab) || 'details')}>
        <Nav.Item><Nav.Link eventKey="details">Details</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="audit">Audit</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="products">Products</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="checks">Checks</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="notes">Notes</Nav.Link></Nav.Item>
      </Nav>

      <div className="distributor-sidebar-body">
        {tab === 'details' && (
          <>
            <div className="distributor-sidebar-actions">
              <Button
                size="sm"
                variant="outline-secondary"
                className="btn-ai"
                disabled={isAiEnriching}
                onClick={onAiEnrich}
              >
                {isAiEnriching ? <Spinner animation="border" size="sm" className="me-1" /> : <Sparkles size={14} />}
                {isAiEnriching ? 'Reading site…' : 'Fill with AI'}
              </Button>
              <Button size="sm" variant="primary" disabled={isSavingDetails} onClick={onSaveDetails}>
                {isSavingDetails ? 'Saving…' : 'Save'}
              </Button>
              {onArchive && (
                <Button size="sm" variant="outline-warning" disabled={isArchiving} onClick={onArchive}>
                  {isArchiving ? 'Archiving…' : 'Archive'}
                </Button>
              )}
              {onDelete && (
                <Button size="sm" variant="outline-danger" disabled={isDeleting} onClick={onDelete}>
                  {isDeleting ? 'Deleting…' : 'Delete'}
                </Button>
              )}
            </div>
            {(aiEnrichStatus || detailsStatus) && (
              <div className={`small mb-2 ${(detailsStatus ?? aiEnrichStatus ?? '').startsWith('Failed') ? 'text-danger' : 'text-muted'}`}>
                {detailsStatus ?? aiEnrichStatus}
              </div>
            )}
            <div className="distributor-sidebar-fields">
              <DistributorInlineField label="Company name" value={detailsDraft.name} onChange={(v) => onDetailsChange((d) => ({ ...d, name: v }))} />
              <Form.Group>
                <Form.Label>Organisation type</Form.Label>
                <Form.Select size="sm" value={detailsDraft.organizationType} onChange={(e) => onDetailsChange((d) => ({ ...d, organizationType: e.target.value as DistributorDto['organizationType'] }))}>
                  <option value="AccreditedDistributor">Accredited distributor</option>
                  <option value="IndependentReseller">Independent reseller / third-party supplier</option>
                  <option value="Unclassified">Unclassified</option>
                </Form.Select>
              </Form.Group>
              <DistributorInlineField label="Company address" value={detailsDraft.address} onChange={(v) => onDetailsChange((d) => ({ ...d, address: v }))} multiline rows={2} />
              <DistributorInlineField label="Phone" value={detailsDraft.phone} type="text" onChange={(v) => onDetailsChange((d) => ({ ...d, phone: v }))} />
              <DistributorInlineField label="Company number" value={detailsDraft.companyNumber} onChange={(v) => onDetailsChange((d) => ({ ...d, companyNumber: v }))} placeholder="Registration no." />
              <DistributorInlineField label="Marketing email" value={detailsDraft.marketingEmail} type="email" onChange={(v) => onDetailsChange((d) => ({ ...d, marketingEmail: v }))} />
              <DistributorInlineField label="Primary contact" value={detailsDraft.primaryContactEmail} type="email" onChange={(v) => onDetailsChange((d) => ({ ...d, primaryContactEmail: v }))} />
              <DistributorInlineField label="Secondary contact" value={detailsDraft.secondaryContactEmail} type="email" onChange={(v) => onDetailsChange((d) => ({ ...d, secondaryContactEmail: v }))} />
              <DistributorInlineField
                label="Website URL"
                value={detailsDraft.websiteUrl}
                type="url"
                onChange={(v) => onDetailsChange((d) => ({ ...d, websiteUrl: v }))}
                onBlurExtra={onWebsiteUrlBlur}
              />
              <DistributorInlineField label="Website country" value={detailsDraft.websiteCountry} onChange={(v) => onDetailsChange((d) => ({ ...d, websiteCountry: v }))} placeholder="GB" />
            </div>
            <p className="distributor-sidebar-section-title">Assignment</p>
            <div className="distributor-sidebar-select-row">
              <Form.Group>
                <Form.Label>Division</Form.Label>
                <Form.Select size="sm" value={detailsDraft.divisionId} onChange={(e) => onDetailsChange((d) => ({ ...d, divisionId: e.target.value }))}>
                  <option value="">Select division…</option>
                  {divisions.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Kingspan business</Form.Label>
                <Form.Select size="sm" value={detailsDraft.businessEntityId} onChange={(e) => onDetailsChange((d) => ({ ...d, businessEntityId: e.target.value }))}>
                  <option value="">Unassigned</option>
                  {businessEntities.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Owner / auditor</Form.Label>
                <Form.Select size="sm" value={detailsDraft.ownerAppUserId} onChange={(e) => onDetailsChange((d) => ({ ...d, ownerAppUserId: e.target.value }))}>
                  <option value="">Unassigned</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{u.displayName || u.email || u.userId}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
          </>
        )}

        {tab === 'audit' && (
          <>
            <p className="distributor-sidebar-section-title">Log contact</p>
            <Form className="audit-form-compact mb-3">
              <Form.Group>
                <Form.Label>When contacted</Form.Label>
                <Form.Control type="datetime-local" size="sm" value={contactAuditDraft.contactedUtc} onChange={(e) => onContactAuditChange((d) => ({ ...d, contactedUtc: e.target.value }))} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Why</Form.Label>
                <Form.Control size="sm" value={contactAuditDraft.reason} onChange={(e) => onContactAuditChange((d) => ({ ...d, reason: e.target.value }))} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Method</Form.Label>
                <Form.Control size="sm" value={contactAuditDraft.contactMethod} onChange={(e) => onContactAuditChange((d) => ({ ...d, contactMethod: e.target.value }))} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Notes</Form.Label>
                <Form.Control as="textarea" rows={2} size="sm" value={contactAuditDraft.notes} onChange={(e) => onContactAuditChange((d) => ({ ...d, notes: e.target.value }))} />
              </Form.Group>
              <Button size="sm" variant="outline-primary" disabled={isLoggingContact || !contactAuditDraft.reason.trim()} onClick={onLogContact}>
                {isLoggingContact ? 'Saving…' : 'Log contact'}
              </Button>
            </Form>

            {isReportLoading && <div className="small text-muted">Loading audit history…</div>}
            {!isReportLoading && distributorReport && (
              <>
                <p className="distributor-sidebar-section-title">Contact history</p>
                <Table size="sm" striped responsive className="mb-3">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Why</th>
                      <th>Method</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {distributorReport.contactAudits.length === 0 && (
                      <tr><td colSpan={4} className="text-muted">No contact audits yet.</td></tr>
                    )}
                    {distributorReport.contactAudits.map((a) => (
                      <tr key={a.id}>
                        <td className="text-nowrap">{new Date(a.contactedUtc).toLocaleDateString()}</td>
                        <td>{a.reason}</td>
                        <td>{a.contactMethod ?? '—'}</td>
                        <td>{a.notes ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <p className="distributor-sidebar-section-title">URLs checked</p>
                <Table size="sm" striped responsive>
                  <thead>
                    <tr>
                      <th>URL</th>
                      <th>Product</th>
                      <th>Checked</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {distributorReport.checkedUrls.length === 0 && (
                      <tr><td colSpan={4} className="text-muted">No checked URLs.</td></tr>
                    )}
                    {distributorReport.checkedUrls.map((u) => (
                      <tr key={u.targetId}>
                        <td><a href={u.url} target="_blank" rel="noreferrer" className="text-truncate d-inline-block" style={{ maxWidth: 140 }}>{u.url}</a></td>
                        <td>{u.productCode}</td>
                        <td className="text-nowrap">{u.lastCheckedUtc ? new Date(u.lastCheckedUtc).toLocaleDateString() : '—'}</td>
                        <td>{u.healthStatus ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </>
        )}

        {tab === 'products' && (
          <>
            {isReportLoading && <div className="small text-muted">Loading products…</div>}
            {!isReportLoading && distributorReport && (
              <div className="distributor-product-list">
                {distributorReport.productsFound.length === 0 && (
                  <div className="text-muted">No products found yet.</div>
                )}
                {distributorReport.productsFound.map((p) => {
                  const needsLink = orphanedProductIds.has(p.productId)
                  return (
                    <article key={p.productId} className="distributor-product-card">
                      <div className="distributor-product-card-main">
                        <span className="distributor-product-code">{p.productCode}</span>
                        <span className="distributor-product-name">{p.productName}</span>
                      </div>
                      <div className="distributor-product-card-actions">
                        <div className="distributor-product-card-field">
                          <span className="distributor-product-field-label">Status</span>
                          {needsLink ? (
                            <Badge bg="warning" text="dark">Needs link</Badge>
                          ) : (
                            <Badge bg="success">Ready</Badge>
                          )}
                        </div>
                        <div className="distributor-product-card-field distributor-product-card-field-due">
                          <span className="distributor-product-field-label">Due</span>
                          <Form.Control
                            type="date"
                            size="sm"
                            value={productDueDateByProductId[p.productId] ?? ''}
                            onChange={(e) => onProductDueDateChange(p.productId, e.target.value)}
                          />
                        </div>
                        <div className="distributor-product-card-buttons">
                          {needsLink ? (
                            <Button size="sm" variant="outline-primary" onClick={() => onLinkProduct(p)}>
                              <Link2 size={12} className="me-1" /> Link
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline-success" onClick={() => onCompareProduct(p.productId)}>
                              <GitCompareArrows size={12} className="me-1" /> Compare
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={isSavingProductAudit}
                            onClick={() => onSaveProductDueDate(p.productId)}
                          >
                            Save due
                          </Button>
                        </div>
                      </div>
                    </article>
                  )
                })}
              </div>
            )}
          </>
        )}

        {tab === 'checks' && (
          <>
            <p className="distributor-sidebar-section-title">Distributor-wide checks</p>
            <div className="small text-muted mb-3">
              Run a one-off compliance check for every product URL currently linked to this distributor.
            </div>
            <Form className="audit-form-compact mb-3">
              <Form.Group>
                <Form.Label>Rule pack</Form.Label>
                <Form.Select size="sm" value={distributorCheckRulePackId} onChange={(e) => onDistributorCheckRulePackChange(e.target.value)}>
                  <option value="">Select rule pack...</option>
                  {rulePacks.map((pack) => (
                    <option key={pack.id} value={pack.id}>
                      {pack.name} v{pack.version}{pack.status !== 'Approved' ? ` (${pack.status})` : ''}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <div className="small text-muted mb-2">
                Legacy ProductComparison distributor checks are disabled. Use Website Compliance Check → Multi-product spotcheck for auditable v2 runs.
              </div>
              <Link className="btn btn-sm btn-outline-primary mb-3" to="/run-checks">
                Open v2 spotcheck
              </Link>
              <Button size="sm" variant="secondary" disabled title="Legacy ProductComparison is deprecated during the v2 rollout.">
                Run check for all linked products (legacy disabled)
              </Button>
            </Form>
            {distributorCheckStatus && (
              <div className="small text-muted mb-3">{distributorCheckStatus}</div>
            )}
            {latestDistributorCheckRunId && (
              <div className="d-flex gap-2 flex-wrap mb-3">
                <Link className="btn btn-sm btn-outline-primary" to={`/check-runs/${latestDistributorCheckRunId}`}>
                  View check history
                </Link>
                <Link className="btn btn-sm btn-outline-secondary" to={`/exception-report?checkRunId=${latestDistributorCheckRunId}`}>
                  View exception report
                </Link>
              </div>
            )}
            {isReportLoading && <div className="small text-muted">Loading linked products...</div>}
            {!isReportLoading && distributorReport && (
              <Table size="sm" striped responsive>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>URLs</th>
                    <th>Due</th>
                  </tr>
                </thead>
                <tbody>
                  {distributorReport.productsFound.length === 0 && (
                    <tr><td colSpan={3} className="text-muted">No linked products found for this distributor.</td></tr>
                  )}
                  {distributorReport.productsFound.map((product) => (
                    <tr key={product.productId}>
                      <td>
                        <span className="d-block fw-semibold">{product.productName}</span>
                        <span className="text-muted" style={{ fontFamily: 'monospace' }}>{product.productCode}</span>
                      </td>
                      <td>{product.checkedUrls.length}</td>
                      <td>{product.nextAuditDueUtc ? new Date(product.nextAuditDueUtc).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}

        {tab === 'notes' && (
          <>
            <div className="distributor-sidebar-actions">
              <Button size="sm" variant="primary" disabled={isSavingDetails} onClick={onSaveDetails}>
                {isSavingDetails ? 'Saving…' : 'Save notes'}
              </Button>
            </div>
            <DistributorInlineField
              label="Notes"
              value={detailsDraft.notes}
              onChange={(v) => onDetailsChange((d) => ({ ...d, notes: v }))}
              multiline
              rows={8}
              emptyText="Click to add notes…"
            />
          </>
        )}
      </div>
    </aside>
  )
}
